import { PoiMediaType } from '@core/dashboard/components/DashboardContent/interfaces';
import { IMillicastStream } from '@core/dashboard/providers/MytaverseEventProvider/hooks/millicast/interfaces';
import { IDolbyMediaStream } from '@core/dashboard/components/DashboardContent/Dolby/interfaces';
import { IStartStreamSessionOutput, StreamSessionStatus } from './gamecast';
import { IZoomParticipantPositionData } from '@core/communications/providers/interfaces';
// Type-only: a value import would pull the event interfaces barrel in here.
import type {
  EventStatus,
  IEventSettings,
  IExperienceFeatureSettings,
  IMapFeatureFlagsSettings,
} from './event';
import type { IMytaverseFile } from './file';
import type { ILiveBrandingEntity } from './map';

export interface IWebSocketConnectionInfo {
  connectionId: string;
  participantId: string;
  loginToken: string;
  eventId?: string;
  roomId?: string;
  region?: string;
  gameSessionId?: string | null;
  spatialType?: SpatialType;
  screenName?: string;
  clientType?: string;
  pairConnectionId?: string;
  dolbySpatialAudioScale?: number;
  muted?: boolean;
  category?: string;
  msg?: string;
}

export enum EFullscreenType {
  Screen = 'SCREEN',
  Camera = 'CAMERA',
}

export enum SpatialType {
  SpatialMeeting = 'SPATIAL_MEETING',
  SpatialAudio = 'SPATIAL_AUDIO',
  OneToMany = 'ONE_TO_MANY',
  DistanceAudio = 'DISTANCE_AUDIO',
  None = 'NONE',
}

export enum PlayerViewUEEnum {
  FirstPerson = 'firstPerson',
  ThirdPerson = 'thirdPerson',
}

export interface PlayerPosition {
  CurrentGameSessionId?: string;
  eventId?: string;
  roomId?: string;
  x: number;
  y: number;
  z: number;
  r: number;
}

export interface ILastWebsocketJsonMessage<T = WebsocketAction> {
  data: IWebSocketConnectionInfo;
  action: T;
  timestamp?: string;
  message?: WebsocketMessage;
  gameSessionId?: string;
  invoiceId?: string; //BILLING_INVOICE
  eventId?: string;
  roomId?: string;
  region?: string;
  regionName?: string;
  regionSpatialType?: SpatialType;
  isSeatRegion?: boolean;
  screenName?: string;
  participantId?: string;
  id?: string;
  width?: string | number;
  height?: string | number;
  gamecastStreamSession?: IStartStreamSessionOutput | null;
  status?: StreamSessionStatus;
  sessionKey?: string;
  fileName?: string;
  asset?: string;
  downloadUrl?: string;
  mediaType?: PoiMediaType;
  muted?: boolean;
  conferenceId?: string;
  mutedBy?: string;
  spatialType?: SpatialType;

  type?: EFullscreenType | string;
  nearestParticipants?: IZoomParticipantPositionData[];
  x?: number;
  y?: number;
  z?: number;
  r?: number;
  mediaStreamId?: string;
  usersinscreen?: string[] | undefined;
  playerPosition?: PlayerPosition;
  dolbySpatialAudioScale?: number;
  // follow me
  followerId?: string;
  ownerId?: string;
  isEventMessage?: boolean;
  followerIds?: string[];
  url: string;
  millicastStreams?: IMillicastStream[];
  dolbyMediaStreams?: IDolbyMediaStream[];
  zoomWebcamScreenId?: string;
  zoomScreenScreenId?: string;
  /** SYNC_DATA_UE / UE: same role as zoomScreenScreenId (no "zoom" in payload). */
  videoScreenScreenId?: string;
  videoWebcamScreenId?: string;
  /** SYNC_DATA_UE: GameLift session ARN on message root. */
  CurrentGameSessionId?: string;
  /**
   * SYNC_DATA_UE (snapshot, 1 Hz) and CHANGE_CURSOR_UE (delta, on change) both
   * carry it on the message root — the cursor belongs to the viewport, not to
   * the pawn, so it is reported even before the character exists. Absent while
   * the `CURSOR_STATE_UE` feature flag is off or the client predates MV-1820.
   */
  cursor?: IUeCursorState;
  audioTrigger?: {
    audioTriggerId?: string;
    videoScreenScreenId?: string;
    videoWebcamScreenId?: string;
    audioType?: string;
    spatialPlayers?: unknown[];
    isSeatRegion?: boolean;
    isDisableWebCamera?: boolean;
  };
  showExitButton?: boolean;

  isHideWebUI?: boolean;
  isHideWebUIExceptAssistant?: boolean;
  isHideAllExceptTopBar?: boolean;

  playerView?: PlayerViewUEEnum;
  zoomScreenStreams?: IDolbyMediaStream[];
  zoomWebcamStreams?: IDolbyMediaStream[];
  speaking?: boolean;
  raisedHand?: boolean;
  worldSoundLevel?: number;
  videoToken?: string;
  videoConferenceName?: string;
  videoUser?: Record<string, unknown>;
  videoOwnerWebcamStream?: {
    participantId?: string;
    videoUserId?: string;
  };
  avatar?: unknown;
  isUEModalActionInProgress?: boolean;
  tag?: string;
  payload?: any;
  name?: string;
  attached?: boolean;
  handRaised?: boolean;
  handRaisedAt?: number;
  poiId?: string;
  hotspotId?: string;
  /** BRANDING_ACTIVATED: the branding the room switched to. */
  brandingId?: string;
  /** ROOM_UPDATED: the room's assistant fallback icon, already signed. */
  assistantIcon?: IMytaverseFile | null;
  /** ROOM_UPDATED: the room's assistant fallback display name. */
  assistantName?: string | null;
  // EventReleaseStatusChanged payload fields
  eventReleaseId?: string;
  /** LOGOUT_REQUEST: free-form origin of the request, logged only (MV-1718). */
  reason?: string;
}

export interface IParticipantPositionMessage {
  timestamp: number;
  eventId: string;
  roomId: string;
  gameSessionId: string;
  regionName?: string;
  regionSpatialType?: SpatialType;
  x: number;
  y: number;
  z: number;
  r: number;
  zoomWebcamScreenId?: string;
  zoomScreenScreenId?: string;
  isHideWebUI?: boolean;
  isHideWebUIExceptAssistant?: boolean;
  isHideAllExceptTopBar?: boolean;
  nearestParticipants?: IZoomParticipantPositionData[];
  isSeatRegion: boolean;
}

export type LastWebsocketJsonMessageType = ILastWebsocketJsonMessage | null;

export enum WebsocketAction {
  ConnectionInfo = 'CONNECTION_INFO',
  ShowSurvey = 'SHOW_SURVEY',
  JoinedToRoom = 'JOINED_TO_ROOM',
  LeaveRoom = 'LEAVE_ROOM',
  LeftRoom = 'LEFT_ROOM',
  JoinedToEvent = 'JOINED_TO_EVENT',
  ParticipantPosition = 'PARTICIPANT_POSITION',
  SyncDataUe = 'SYNC_DATA_UE',
  AssistantIcon = 'ASSISTANT_ICON',
  LeftEvent = 'LEFT_EVENT',
  ExitEvent = 'EXIT_EVENT',
  JoinToRegion = 'JOIN_TO_REGION',
  JoinedToRegion = 'JOINED_TO_REGION',
  TeleportToLocation = 'TELEPORT_TO_LOCATION',
  LeaveRegion = 'LEAVE_REGION',
  LeftRegion = 'LEFT_REGION',
  ChangeRoomScale = 'CHANGE_ROOM_SCALE',
  BrowserUrlRequest = 'BROWSER_URL_REQUEST',
  MuteParticipant = 'MUTE_PARTICIPANT',
  ShowParticipantInfo = 'SHOW_PARTICIPANT_INFO',
  SendToBriefcase = 'SEND_TO_BRIEFCASE',
  ChangeParticipantProfile = 'CHANGE_PARTICIPANT_PROFILE',
  ParticipantRoleUpdated = 'PARTICIPANT_ROLE_UPDATED',
  MessageToWebapp = 'MESSAGE_TO_WEBAPP',
  MessageToRoom = 'MESSAGE_TO_ROOM',
  CloseGame = 'CLOSE_GAME',
  StartGuidedTutorial = 'START_GUIDED_TUTORIAL',
  StartShareVideoRequest = 'START_SHARE_VIDEO_REQUEST',
  StopShareVideoRequest = 'STOP_SHARE_VIDEO_REQUEST',

  StartShareScreenRequest = 'START_SCREEN_VIDEO_REQUEST',
  StopShareScreenRequest = 'STOP_SCREEN_VIDEO_REQUEST',

  ShowMillicastScreenBroadcastingDataRequest = 'SHOW_MILLICAST_SCREEN_BROADCASTING_DATA_REQUEST',

  Ue5WsConnected = 'UE5_WS_CONNECTED',
  Ue5WsDisconnected = 'UE5_WS_DISCONNECTED',
  FirstPersonViewRegion = 'FIRST_PERSON_VIEW_REGION',
  FreePersonViewRegion = 'FREE_PERSON_VIEW_REGION',
  ParticipantMutedState = 'PARTICIPANT_MUTED_STATE',
  MuteParticipantState = 'MUTE_PARTICIPANT_STATE',
  MuteConference = 'MUTE_CONFERENCE',
  StopDolbyScreenSharing = 'STOP_DOLBY_SCREEN_SHARING',
  ParticipantCameraPosition = 'PARTICIPANT_VIDEO_POSITION',
  ToggleLevelEditor = 'TOGGLE_LEVEL_EDITOR',

  SyncDataWeb = 'SYNC_DATA_WEB',

  EnableDroneCamera = 'ENABLE_DRONE_CAMERA',
  DisableDroneCamera = 'DISABLE_DRONE_CAMERA',

  Hoverboard = 'HOVERBOARD',
  LaserPointer = 'LASER_POINTER',
  Magnifier = 'MAGNIFIER',
  BeamMeUp = 'BEAM_ME_UP',
  TeleportToIdLocation = 'TELEPORT_TO_ID_LOCATION',
  SetupKeyboard = 'SETUP_KEYBOARD',
  FeatureFlags = 'FEATURE_FLAGS',

  TeleportToParticipant = 'TELEPORT_TO_PARTICIPANT',
  TeleportToRoom = 'TELEPORT_TO_ROOM',
  OwnerTeleport = 'OWNER_TELEPORT_TO_ROOM',

  PlayVideo = 'PLAY_VIDEO',

  FollowPlayer = 'FOLLOW_PLAYER',
  InviteFollowingByAdmin = 'INVITE_FOLLOWING_BY_ADMIN',
  StopFollowingByAdmin = 'STOP_FOLLOWING_BY_ADMIN',
  CancelInviteFollowingByAdmin = 'CANCEL_INVITE_FOLLOWING_BY_ADMIN',
  AcceptFollowing = 'ACCEPT_FOLLOWING',
  DisengageFollowing = 'DISENGAGE_FOLLOWING',
  StopFollowing = 'STOP_FOLLOWING',
  OpenTeleportModal = 'OPEN_TELEPORT_MODAL',
  CloseTeleportModal = 'CLOSE_TELEPORT_MODAL',
  TeleportToRegion = 'TELEPORT_TO_REGION',
  TeleportFinished = 'TELEPORT_FINISHED',
  ClientReady = 'CLIENT_READY',

  BillingOpen = 'BILLING_OPEN',
  BillingBeat = 'BILLING_BEAT',
  BillingClose = 'BILLING_CLOSE',
  BillingStale = 'BILLING_STALE',
  BillingInvoice = 'BILLING_INVOICE',

  ShowNotification = 'SHOW_NOTIFICATION',
  TerminateSession = 'TERMINATE_SESSION',
  /** MV-1718 — UE asks the hub to log the user out and end the stream session. */
  LogoutRequest = 'LOGOUT_REQUEST',
  ShowDemoNotification = 'SHOW_DEMO_NOTIFICATION',
  ShowWebNotification = 'SHOW_WEB_NOTIFICATION',

  ToggleExitButton = 'TOGGLE_EXIT_BUTTON',

  AvatarRequested = 'SELECT_AVATAR_REQUEST',
  AvatarReceived = 'AVATAR_RECEIVED',
  AvatarSelect = 'SELECT_AVATAR',

  MytaverseAction = 'MYTA_EVENT',
  GamecastStreamSession = 'GAMECAST_STREAM_SESSION',

  GamecastSessionTerminated = 'GAMECAST_SESSION_TERMINATED',

  // Create Avatar
  PreloadAvatar = 'PRELOAD_AVATAR',
  ReloadAvatar = 'CHANGE_AVATAR',

  // UE Verbosity
  UEError = 'UE_ERROR',
  UEWarn = 'UE_WARN',
  UEDebug = 'UE_DEBUG',
  UEInfo = 'UE_INFO',
  FullscreenSharing = 'START_FULLSCREEN_SHARING',
  OpenOfficeHours = 'OPEN_OFFICE_HOURS',

  // Map Modal
  ShowHubMap = 'SHOW_HUB_MAP',
  HideHubMap = 'SHOW_HUB_MAP',
  MButtonClick = 'M_BUTTON_CLICK',

  //3D Assets
  Asset3DLoad = '3D_ASSET_LOAD',
  Asset3DStop = '3D_ASSET_STOP',
  Asset3DSetDimension = '3D_ASSET_SET_DIMENSION',
  Asset3DReady = '3D_ASSET_READY',
  Asset3DNewLimits = '3D_ASSET_CHANGE_LIMITS',
  Asset3DNewFailed = '3D_ASSET_LOAD_FAILED',
  Asset3DExploration = '3D_ASSET_ALLOW_EXPLORATION',
  Asset3DMaxSize = '3D_ASSET_MAX_SIZE',

  EventStage = 'EVENT_STAGE',

  TakeScreenshotRequest = 'TAKE_SCREENSHOT_REQUEST',
  TakeScreenshotFinish = 'TAKE_SCREENSHOT_FINISH',
  TakeScreenshotFailed = 'TAKE_SCREENSHOT_FAILED',

  ExternalKeyboard = 'EXTERNAL_KEYBOARD',

  CustomisationItemSelected = 'CUSTOMISATION_ITEM_SELECTED',
  RaiseHandState = 'RAISE_HAND_STATE',

  StopEntireSession = 'STOP_ENTIRE_SESSION',

  MovementCollisionIdentified = 'MOVEMENT_COLLISION_IDENTIFIED',

  AssistantScriptUpdated = 'ASSISTANT_SCRIPT_UPDATED',

  EventSettingsUpdated = 'EVENT_SETTINGS_UPDATED',

  ObjectPoiClosed = '3D_POI_CLOSED',
  Hotspot3DClosed = '3D_HOTSPOT_CLOSED',
  ShowHotspotPreview = 'SHOW_HOTSPOT_PREVIEW',
  StartHotspotPlace2D = 'START_HOTSPOT_PLACE_2D',
  StartHotspotPlace3D = 'START_HOTSPOT_PLACE_3D',
  FinishHotspotPlace2D = 'FINISH_HOTSPOT_PLACE_2D',
  FinishHotspotPlace3D = 'FINISH_HOTSPOT_PLACE_3D',

  StartViewHotspot = 'START_VIEW_HOTSPOT',
  EndViewHotspot = 'END_VIEW_HOTSPOT',
  StartHotspotEdit = 'START_HOTSPOT_EDIT',
  EndHotspotEdit = 'END_HOTSPOT_EDIT',

  // Real-time entity spawning. All five are hub → engine except
  // UpdateRealtimeEntity, which the engine also sends back as the settled
  // transform report after a gizmo drag — same action name, same payload shape.
  SpawnRealtimeEntities = 'SPAWN_REALTIME_ENTITIES',
  UpdateRealtimeEntity = 'UPDATE_REALTIME_ENTITY',
  DeleteRealtimeEntity = 'DELETE_REALTIME_ENTITY',
  StartRealtimeEntityEdit = 'START_REALTIME_ENTITY_EDIT',
  EndRealtimeEntityEdit = 'END_REALTIME_ENTITY_EDIT',

  // Unreal's reply after every SPAWN_LAYOUT and CLEAR_LAYOUT — what actually got
  // built, which is shorter than the request when a room was refused for landing
  // on top of one already standing.
  CurrentGeometryList = 'CURRENT_GEOMETRY_LIST',

  // The same answer for a *draft*: which rows of the preview Unreal could not place.
  // Carries no room list, because a draft is one author's and describes nothing the
  // other participants are in. Without it a refused draft row is silent — the
  // preview simply stops producing geometry partway down the chain.
  LayoutPreviewRefused = 'LAYOUT_PREVIEW_REFUSED',

  EditorModeState = 'EDITOR_MODE_STATE',

  SpotFullscreenView = 'SPOT_FULLSCREEN_VIEW',

  // The other half of SPOT_FULLSCREEN_VIEW, and the only one that travels
  // hub → UE. Opening the overlay ducks that spot's in-world audio on the UE
  // side, and this message is the *only* thing that restores it — there is no
  // timeout and no other recovery path, so a spot whose close never arrives
  // stays silent until the participant rejoins. It must therefore be sent on
  // every close path (X, ESC, backdrop, browser back, navigating away, opening
  // another spot, a programmatic close) and for image spots as well as video.
  // UE tolerates a duplicate, a close with nothing open, and a close with no
  // `continueVideoFrom`; when in doubt, send it.
  SpotFullscreenViewClosed = 'SPOT_FULLSCREEN_VIEW_CLOSED',

  // Universal branding: per-entity property overrides pushed to UE. An entity
  // that is sent gets exactly the properties listed; CLEAR_BRANDING drops every
  // override so the level's authored values show again.
  BrandingUpdated = 'BRANDING_UPDATED',
  ClearBranding = 'CLEAR_BRANDING',

  // The other half of universal branding, and the only one that travels the
  // other way: UE naming the brandable objects standing in the world right now
  // (MV-1905). Sent unprompted whenever that set changes — a map change, a
  // spawn or a destroy, a level streamed in or out — debounced by half a second
  // on the UE side, and relayed to the hub by the backend's default UE5→webapp
  // fan-out like CURRENT_GEOMETRY_LIST is.
  //
  // Always the whole world, never a delta: replace what is held, do not merge.
  // Values are still saved and pushed by `brandingId` through BRANDING_UPDATED,
  // and a push may precede the object — UE holds it and applies it on spawn.
  BrandingEntities = 'BRANDING_ENTITIES',

  // The one branding action that arrives *from* the backend rather than going
  // to UE: the room's live branding changed, and every hub in that room is told
  // which one it is now. UE is nudged with SPOTS_UPDATED instead, so the two
  // clients never see each other's message. The hub also sends this same action
  // to UE through MESSAGE_TO_UNREAL, which only ever fans out to UE5
  // connections — it does not come back here.
  BrandingActivated = 'BRANDING_ACTIVATED',

  // Also backend → hub only: a branding's saved real-time entities changed, so
  // every other admin's editor knows to load the set again. The engine is
  // nudged with SPOTS_UPDATED instead, the same split as BRANDING_ACTIVATED.
  SpawnedEntitiesUpdated = 'SPAWNED_ENTITIES_UPDATED',

  // The room's assistant fallback identity was edited in the CMS. An
  // allow-listed pair rather than the whole room: the hub keeps its own
  // `gameSessionId` on `currentRoom`, and that object's identity drives the
  // branding-spot / hotspot / POI effects, so a wholesale merge would both
  // clobber the session id and make markers flicker on every edit.
  RoomUpdated = 'ROOM_UPDATED',

  EventReleaseStatusChanged = 'EVENT_RELEASE_STATUS_CHANGED',
  EventChanged = 'EVENT_CHANGED',

  // MV-1820 — the cursor shape UE wants over the stream, the moment it changes.
  // The same `cursor` object also rides on every SYNC_DATA_UE heartbeat; this
  // action exists only so hover feedback does not wait up to a second for the
  // next one. It is an event, not a second synchroniser: absent fields mean
  // "this message is not about them", never "cleared".
  ChangeCursorUe = 'CHANGE_CURSOR_UE',
}

/**
 * Cursor state UE reports for the streamed viewport (MV-1820).
 *
 * `state` is already a CSS `cursor` keyword — `default`, `pointer`, `none`,
 * `text`, `move`, `grab`, `grabbing`, `crosshair`, `not-allowed` and the four
 * `*-resize` handles today — so no lookup table is needed. The vocabulary is
 * deliberately open: UE may add keywords without a web release, which is why
 * nothing here switches on it exhaustively.
 */
export interface IUeCursorState {
  state?: string;
  /** Derived (`state !== 'none'`); only a page-drawn cursor would need it. */
  isVisible?: boolean;
}

/**
 * Allow-listed event fields the backend broadcasts over `EVENT_CHANGED`.
 * Deliberately not the whole record: the carousel reads experiences through
 * GET /api/v1/profile/events, which joins the release and normalizes asset
 * URLs, so a raw item is a different shape and cannot replace a loaded card.
 */
export interface IEventChangedFields {
  status: EventStatus;
  eventReleaseId: string | null;
  clientVersion: string | null;
  clientChangeset: string | null;
  isPublished: boolean;
}

export interface IEventChangedPayload {
  action: WebsocketAction.EventChanged;
  eventId: string;
  worldId: string;
  changed: (keyof IEventChangedFields)[];
  event: IEventChangedFields;
  timestamp: number;
}

/**
 * Allow-listed `settings` fields the backend broadcasts over
 * `EVENT_SETTINGS_UPDATED`. Only the keys the hub reacts to live in here — the
 * whole settings record is never pushed, so an absent key means "unchanged",
 * not "cleared".
 */
export type IEventSettingsUpdatedFields = Pick<
  IEventSettings,
  'aiVirtualAssistantName'
>;

export interface IEventSettingsUpdatedPayload {
  action: WebsocketAction.EventSettingsUpdated;
  settings?: IEventSettingsUpdatedFields;
  features?: IExperienceFeatureSettings;
  mapFeatureFlags?: IMapFeatureFlagsSettings;
}

/**
 * The brandable objects UE reports standing in the world (`BRANDING_ENTITIES`).
 *
 * The list is the whole world every time, so it replaces what the hub holds
 * rather than merging into it. UE drops duplicate `brandingId`s before sending,
 * so the ids in one frame are unique.
 */
export interface IBrandingEntitiesPayload {
  action: WebsocketAction.BrandingEntities;
  entities?: ILiveBrandingEntity[];
}

export enum WebsocketMessage {
  ShowControls = 'SHOW_CONTROLS',
  HideControls = 'HIDE_CONTROLS',
}

export enum WebsocketErrorCode {
  CloseAbnormal = 1006,
}

export enum WebSocketUECategory {
  UEAvatarLoadError = 'UE_ERROR_AVATAR_LOAD',
}
